import CemeteryRequest from "@/components/CemeteryRequest";

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, uploadPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Loading } from "@/components/common";
import BackNav from "@/components/BackNav";
import CemeteryMap from "@/components/CemeteryMap";

export default function NuovaTomba() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [cemeteries, setCemeteries] = useState([]);
  const [comune, setComune] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posConfirmed, setPosConfirmed] = useState(false);
  const [form, setForm] = useState({
    address: "", deceased_name: "", deceased_surname: "", grave_type: "lapide",
    cemetery_id: "", campo: "", fila: "", numero: "", photo: "", map_x: null, map_y: null, notes: "",
  });

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/cemeteries").then((r) => {
      setCemeteries(r.data);
      if (r.data.length > 0) {
        setComune(r.data[0].comune);
        setForm((f) => ({ ...f, cemetery_id: r.data[0].id }));
      }
    });
  }, []);

  if (!config) return <Loading />;

  const comuni = [...new Set(cemeteries.map((c) => c.comune))];
  const cemeteriesOfComune = cemeteries.filter((c) => c.comune === comune);
  const cemetery = cemeteries.find((c) => c.id === form.cemetery_id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onComune = (e) => {
    setComune(e.target.value);
    const first = cemeteries.find((c) => c.comune === e.target.value);
    if (first) setForm((f) => ({ ...f, cemetery_id: first.id }));
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file);
      setForm((f) => ({ ...f, photo: url }));
      toast.success("Fotografia caricata.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/customer/graves", { ...form, position_confirmed: posConfirmed });
      toast.success("Luogo registrato.");
      const next = params.get("next");
      navigate(next || `/area-cliente/tombe/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="new-grave-page">
      <div className="mb-6"><BackNav /></div>
      <p className="eyebrow mb-2">Area riservata</p>
      <h1 className="font-serif text-4xl text-navy mb-2">Registra un luogo</h1>
      <p className="text-sm text-body mb-8">Queste informazioni ci permettono di trovare il luogo nel cimitero e prendercene cura.</p>

      <form onSubmit={submit} className="space-y-8">
        <div className="card-soft p-6 space-y-4">
          <p className="eyebrow">I tuoi dati</p>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div><p className="label-soft">Nome e cognome</p><p className="text-navy font-medium" data-testid="owner-name">{user?.name} {user?.surname}</p></div>
            <div><p className="label-soft">Email</p><p className="text-navy font-medium" data-testid="owner-email">{user?.email}</p></div>
            <div><p className="label-soft">Telefono</p><p className="text-navy font-medium" data-testid="owner-phone">{user?.phone || "—"}</p></div>
            <Field label="Indirizzo (facoltativo)" testid="field-address">
              <input data-testid="input-address" className="input-soft" value={form.address} onChange={set("address")} />
            </Field>
          </div>
        </div>

        <div className="card-soft p-6 space-y-4">
          <p className="eyebrow">La persona cara</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome" testid="field-deceased-name">
              <input data-testid="input-deceased-name" className="input-soft" value={form.deceased_name} onChange={set("deceased_name")} required />
            </Field>
            <Field label="Cognome" testid="field-deceased-surname">
              <input data-testid="input-deceased-surname" className="input-soft" value={form.deceased_surname} onChange={set("deceased_surname")} required />
            </Field>
            <Field label="Tipo di luogo" testid="field-grave-type">
              <select data-testid="select-grave-type" className="input-soft" value={form.grave_type} onChange={set("grave_type")}>
                {config.grave_types.map((t) => (
                  <option key={t} value={t}>{t === "lapide" ? "Lapide" : t === "loculo" ? "Loculo" : t === "cappella" ? "Cappella" : t}</option>
                ))}
              </select>
            </Field>
            <Field label="Comune" testid="field-comune">
              <select data-testid="select-comune" className="input-soft" value={comune} onChange={onComune}>
                {comuni.map((cm) => (
                  <option key={cm} value={cm}>{cm}</option>
                ))}
              </select>
            </Field>
            <Field label="Cimitero" testid="field-cemetery">
              <select data-testid="select-cemetery" className="input-soft" value={form.cemetery_id} onChange={set("cemetery_id")}>
                {cemeteriesOfComune.map((c) => (
                  <option key={c.id} value={c.id}>{`${c.name} — ${c.address}`}</option>
                ))}
              </select>
            </Field>
            <Field label="Campo" testid="field-campo">
              <input data-testid="input-campo" className="input-soft" value={form.campo} onChange={set("campo")} placeholder="Es. B" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Fila" testid="field-fila">
                <input data-testid="input-fila" className="input-soft" value={form.fila} onChange={set("fila")} placeholder="Es. 4" />
              </Field>
              <Field label="Numero lapide/loculo" testid="field-numero">
                <input data-testid="input-numero" className="input-soft" value={form.numero} onChange={set("numero")} placeholder="Es. 12" />
              </Field>
            </div>
          </div>
          <div className="mt-4">
            <CemeteryRequest testid="nuovatomba-cemetery-request" />
          </div>
        </div>

        <div className="card-soft p-6 space-y-4">
          <p className="eyebrow">Posizione sulla mappa</p>
          <CemeteryMap
            image={cemetery?.aerial_image}
            x={form.map_x}
            y={form.map_y}
            onChange={(x, y, confirmed) => {
              setForm((f) => ({ ...f, map_x: x, map_y: y }));
              setPosConfirmed(confirmed);
            }}
          />
          {cemetery?.notes && <p className="text-xs text-muted-soft">Note sul cimitero: {cemetery.notes}</p>}
        </div>

        <div className="card-soft p-6 space-y-4">
          <p className="eyebrow">Fotografia del luogo</p>
          <input data-testid="input-grave-photo" type="file" accept="image/*" onChange={onPhoto} className="text-sm text-body" />
          {uploading && <p className="text-xs text-muted-soft">Caricamento in corso…</p>}
          {form.photo && <img src={form.photo} alt="Foto del luogo" data-testid="grave-photo-preview" className="w-full max-w-sm rounded-xl border border-[#e2e8f0]" />}
          <Field label="Note (facoltative)" testid="field-grave-notes">
            <textarea data-testid="input-grave-notes" className="input-soft" rows={3} value={form.notes} onChange={set("notes")} placeholder="Qualsiasi dettaglio utile: colore del marmo, oggetti presenti, accessi…" />
          </Field>
        </div>

        <button data-testid="grave-submit" type="submit" disabled={busy || uploading || !posConfirmed} className="btn-primary w-full disabled:opacity-60">
          {busy ? "Salvataggio…" : "Salva il luogo"}
        </button>
        {!posConfirmed && (
          <p data-testid="position-required-hint" className="text-xs text-muted-soft text-center">
            Per aiutarci a trovare facilmente il luogo, indica e conferma prima la sua posizione sulla mappa.
          </p>
        )}
      </form>
    </div>
  );
}
